import axios from 'axios';
import {
    clearAuthStorage,
    dispatchSessionExpired,
    getStoredRefreshToken,
    hasStoredAuthSession,
    isIdleSessionExpired
} from '../utils/authSession';

// ============================================================================
// 1. AXIOS CONFIGURATION & INTERCEPTORS
// ============================================================================

// Get API URL from environment variables
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.sanspath.co.uk/api/v1';

console.log('API Base URL:', API_BASE_URL);

// Create axios instance
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 300000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Helper function to get auth token
export const getAuthToken = () => {
    try {
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        if (token) return token;

        try {
            const { default: useAuthStore } = require('../store/authStore');
            const { token: storeToken } = useAuthStore.getState();
            return storeToken;
        } catch (importError) {
            console.warn('Could not import authStore:', importError.message);
            return null;
        }
    } catch (error) {
        console.warn('Error getting auth token:', error.message);
        return null;
    }
};

// Helper function to get refresh token
const getRefreshToken = () => {
    try {
        const refreshToken = getStoredRefreshToken();
        if (refreshToken) return refreshToken;

        try {
            const { default: useAuthStore } = require('../store/authStore');
            const { refreshToken: storeRefreshToken } = useAuthStore.getState();
            return storeRefreshToken;
        } catch (importError) {
            console.warn('Could not import authStore for refresh token:', importError.message);
            return null;
        }
    } catch (error) {
        console.warn('Error getting refresh token:', error.message);
        return null;
    }
};

let refreshTokenRequest = null;

const AUTH_ENDPOINTS_WITHOUT_REFRESH = [
    '/auth/login',
    '/auth/register',
    '/auth/refresh-token',
    '/auth/logout',
    '/auth/forgot-password',
    '/auth/reset-password'
];

const isRefreshSkippedEndpoint = (url = '') => {
    const normalizedUrl = String(url || '');
    return AUTH_ENDPOINTS_WITHOUT_REFRESH.some((endpoint) => normalizedUrl.includes(endpoint));
};

const revokeRefreshToken = (refreshToken) => {
    if (!refreshToken) return;
    axios.post(`${API_BASE_URL}/auth/logout`, { refreshToken }, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000
    }).catch((error) => {
        console.warn('Failed to revoke refresh token:', error?.message || error);
    });
};

const clearClientAuthSession = (reason = 'auth_failed', revokeToken = false) => {
    const refreshToken = getRefreshToken();
    if (revokeToken) {
        revokeRefreshToken(refreshToken);
    }

    try {
        const { default: useAuthStore } = require('../store/authStore');
        useAuthStore.getState().logout({ callApi: false, reason });
    } catch (storeError) {
        console.warn('Could not update authStore during auth clear:', storeError.message);
        clearAuthStorage();
    }

    dispatchSessionExpired(reason);
};

const refreshAccessToken = async () => {
    if (refreshTokenRequest) return refreshTokenRequest;

    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error('No refresh token available');

    refreshTokenRequest = axios.post(`${API_BASE_URL}/auth/refresh-token`, { refreshToken }, {
        headers: { 'Content-Type': 'application/json' }
    }).then((refreshResponse) => {
        const newToken = refreshResponse.data.token || refreshResponse.data.accessToken;
        const newRefreshToken = refreshResponse.data.refreshToken;

        if (!newToken) {
            throw new Error('No access token returned from refresh');
        }

        localStorage.setItem('authToken', newToken);
        localStorage.setItem('token', newToken);
        if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);

        try {
            const { default: useAuthStore } = require('../store/authStore');
            useAuthStore.getState().setToken(newToken, newRefreshToken);
        } catch (storeError) {
            console.warn('Could not update authStore with new token:', storeError.message);
        }

        return {
            token: newToken,
            refreshToken: newRefreshToken
        };
    }).finally(() => {
        refreshTokenRequest = null;
    });

    return refreshTokenRequest;
};

export const createClientRequestId = (prefix = 'web') => {
    const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return `${prefix}-${random}`;
};

export const logApiJson = (event, payload = {}) => {
    try {
        console.info('API_JSON', JSON.stringify({
            event,
            timestamp: new Date().toISOString(),
            ...payload
        }, null, 2));
    } catch (_error) {
        console.info('API_JSON', event, payload);
    }
};

const getConfigHeader = (headers, name) => {
    if (!headers) return null;
    if (typeof headers.get === 'function') return headers.get(name);
    return headers[name] || headers[name.toLowerCase()] || null;
};

const setConfigHeader = (headers, name, value) => {
    if (!headers) return;
    if (typeof headers.set === 'function') {
        headers.set(name, value);
        return;
    }
    headers[name] = value;
};

const deleteConfigHeader = (headers, name) => {
    if (!headers) return;
    if (typeof headers.delete === 'function') {
        headers.delete(name);
        return;
    }
    delete headers[name];
    delete headers[name.toLowerCase()];
};

const SENSITIVE_FIELD_PATTERN = /(password|token|authorization|secret|code|otp)/i;

const redactSensitiveData = (value) => {
    if (!value || typeof value !== 'object') return value;
    if (typeof FormData !== 'undefined' && value instanceof FormData) return '[FormData]';
    if (Array.isArray(value)) return value.map(redactSensitiveData);

    return Object.entries(value).reduce((result, [key, entryValue]) => {
        result[key] = SENSITIVE_FIELD_PATTERN.test(key)
            ? '[REDACTED]'
            : redactSensitiveData(entryValue);
        return result;
    }, {});
};

const isBrowserFile = (value) => typeof File !== 'undefined' && value instanceof File;
const isBrowserFileList = (value) => typeof FileList !== 'undefined' && value instanceof FileList;

const appendOfferField = (formData, key, value) => {
    if (value === undefined || value === null || value === '') return;

    if (isBrowserFile(value)) {
        formData.append(key, value);
        return;
    }

    if (key === 'additionalDocuments' && (Array.isArray(value) || isBrowserFileList(value))) {
        Array.from(value).forEach((file) => {
            if (isBrowserFile(file)) formData.append('additionalDocuments', file);
        });
        return;
    }

    if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
        return;
    }

    formData.append(key, String(value));
};

const buildOfferFormData = (offerData = {}) => {
    if (offerData instanceof FormData) return offerData;

    const formData = new FormData();
    Object.entries(offerData).forEach(([key, value]) => appendOfferField(formData, key, value));
    return formData;
};

const getValidationDetails = (errorData = {}) => {
    if (Array.isArray(errorData.details)) return errorData.details;
    if (Array.isArray(errorData.errors)) return errorData.errors;
    return [];
};

const getValidationDetailMessage = (detail = {}) => {
    if (typeof detail === 'string') return detail;
    return (
        detail.message ||
        detail.msg ||
        detail.error ||
        ''
    );
};

// Request interceptor
apiClient.interceptors.request.use(
    (config) => {
        config.headers = config.headers || {};
        config.metadata = {
            ...(config.metadata || {}),
            startedAt: Date.now()
        };
        const requestId = getConfigHeader(config.headers, 'X-Request-Id') || createClientRequestId();
        setConfigHeader(config.headers, 'X-Request-Id', requestId);
        config.metadata.requestId = requestId;
        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);

        const isFormDataPayload = typeof FormData !== 'undefined' && config.data instanceof FormData;
        let contentType = getConfigHeader(config.headers, 'Content-Type');
        if (isFormDataPayload && String(contentType || '').toLowerCase().startsWith('application/json')) {
            deleteConfigHeader(config.headers, 'Content-Type');
            contentType = getConfigHeader(config.headers, 'Content-Type');
        }
        const isMultipart = String(contentType || '').toLowerCase().startsWith('multipart/form-data') || isFormDataPayload;
        logApiJson('api_request_started', {
            requestId,
            method: config.method?.toUpperCase(),
            url: config.url,
            baseURL: config.baseURL,
            operation: config.metadata.operation || null,
            isMultipart,
            timeout: config.timeout || apiClient.defaults.timeout,
            dataKeys: config.data && !isMultipart && typeof config.data === 'object' ? Object.keys(config.data) : [],
            file: config.metadata.file || null
        });

        if (config.data && !isMultipart) {
            console.log('📦 Request data:', redactSensitiveData(config.data));
        }

        if (!config.skipIdleCheck && hasStoredAuthSession() && isIdleSessionExpired()) {
            clearClientAuthSession('idle_timeout', true);
            return Promise.reject({
                message: 'Session expired after 30 minutes of inactivity. Please log in again.',
                code: 'SESSION_EXPIRED'
            });
        }

        const token = getAuthToken();
        if (!config.skipAuthHeader && token) {
            setConfigHeader(config.headers, 'Authorization', `Bearer ${token}`);
            console.log('🔑 Added auth token to request');
        } else if (!config.skipAuthHeader) {
            console.warn('⚠️ No auth token found');
        }
        return config;
    },
    (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor
apiClient.interceptors.response.use(
    (response) => {
        console.log(`✅ API Response: ${response.status} ${response.config.url}`);
        const requestId = response.headers?.['x-request-id'] || response.config?.metadata?.requestId || null;
        logApiJson('api_response_completed', {
            requestId,
            method: response.config?.method?.toUpperCase(),
            url: response.config?.url,
            operation: response.config?.metadata?.operation || null,
            status: response.status,
            durationMs: response.config?.metadata?.startedAt ? Date.now() - response.config.metadata.startedAt : null,
            responseRequestId: response.data?.requestId || response.data?.metadata?.requestId || null,
            responseKeys: response.data && typeof response.data === 'object' ? Object.keys(response.data) : []
        });
        if (response.data) console.log('📦 Response data:', redactSensitiveData(response.data));
        return response.data;
    },
    async (error) => {
        const original = error.config;
        console.error(`❌ API Error: ${error.response?.status || 'Network Error'} ${original?.url}`);
        const requestId = error.response?.headers?.['x-request-id'] || original?.metadata?.requestId || null;
        logApiJson('api_response_failed', {
            requestId,
            method: original?.method?.toUpperCase(),
            url: original?.url,
            operation: original?.metadata?.operation || null,
            status: error.response?.status || null,
            durationMs: original?.metadata?.startedAt ? Date.now() - original.metadata.startedAt : null,
            code: error.code || null,
            message: error.message,
            response: redactSensitiveData(error.response?.data) || null
        });

        if (error.response?.data) console.error('📦 Error response:', redactSensitiveData(error.response.data));

        const shouldAttemptRefresh = error.response?.status === 401 &&
            original &&
            !original._retry &&
            !original.skipAuthRefresh &&
            !isRefreshSkippedEndpoint(original.url);

        // Handle 401 unauthorized errors (Token Refresh)
        if (shouldAttemptRefresh) {
            original._retry = true;
            console.log('🔄 Attempting token refresh...');

            try {
                if (hasStoredAuthSession() && isIdleSessionExpired()) {
                    throw new Error('Session expired due to inactivity');
                }

                const refreshed = await refreshAccessToken();

                setConfigHeader(original.headers, 'Authorization', `Bearer ${refreshed.token}`);
                console.log('✅ Token refreshed, retrying request...');
                return apiClient(original);
            } catch (refreshError) {
                console.error('❌ Token refresh failed:', refreshError);
                clearClientAuthSession(
                    refreshError?.message?.includes('inactivity') ? 'idle_timeout' : 'auth_failed',
                    refreshError?.message?.includes('inactivity')
                );

                return Promise.reject({
                    message: refreshError?.message?.includes('inactivity')
                        ? 'Session expired after 30 minutes of inactivity. Please log in again.'
                        : 'Authentication failed. Please log in again.',
                    code: refreshError?.message?.includes('inactivity') ? 'SESSION_EXPIRED' : 'AUTH_FAILED'
                });
            }
        }

        if (!error.response) {
            return Promise.reject({
                message: 'Unable to connect to the server. Please check your internet connection and try again.',
                code: 'NETWORK_ERROR'
            });
        }

        // Handle standard HTTP errors
        if (error.response.status === 400) {
            const errorData = error.response.data;
            const validationDetails = getValidationDetails(errorData);
            if (validationDetails.length) {
                const firstDetailMessage = validationDetails
                    .map(getValidationDetailMessage)
                    .find(Boolean);

                return Promise.reject({
                    message: errorData.message || firstDetailMessage || errorData.error || 'Validation failed',
                    error: errorData.error,
                    details: validationDetails,
                    errors: validationDetails,
                    status: 400,
                    code: errorData.code || 'VALIDATION_ERROR',
                    response: errorData
                });
            }
            return Promise.reject({
                message: errorData.message || errorData.error || 'Invalid request data',
                details: errorData.details,
                status: 400,
                code: errorData.code || 'BAD_REQUEST',
                response: errorData
            });
        }

        const errorMessage = error.response.data?.message || error.response.data?.error || error.message || 'An unexpected error occurred';
        return Promise.reject({
            message: errorMessage,
            status: error.response.status,
            code: 'HTTP_ERROR',
            response: error.response.data
        });
    }
);

// ============================================================================
// 2. API ENDPOINT DEFINITIONS
// ============================================================================

// Auth API
export const authAPI = {
    register: (userData) => {
        console.log('API: Sending registration request to /auth/register');
        return apiClient.post('/auth/register', userData);
    },
    login: (credentials) => {
        console.log('API: Sending login request to /auth/login');
        return apiClient.post('/auth/login', credentials);
    },
    logout: (refreshToken = getRefreshToken()) => {
        console.log('API: Sending logout request');
        return apiClient.post('/auth/logout', { refreshToken }, {
            skipAuthHeader: true,
            skipAuthRefresh: true,
            skipIdleCheck: true
        });
    },
    refreshToken: (refreshToken = getRefreshToken()) => {
        console.log('API: Refreshing token');
        return apiClient.post('/auth/refresh-token', { refreshToken }, {
            skipAuthHeader: true,
            skipAuthRefresh: true
        });
    },
    submitKYC: (documents) => {
        console.log('API: Submitting KYC documents');
        const formData = new FormData();
        Object.keys(documents).forEach(key => {
            if (documents[key] instanceof File) {
                formData.append(key, documents[key]);
            } else {
                formData.append(key, JSON.stringify(documents[key]));
            }
        });
        return apiClient.post('/employers/kyc/documents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    submitKycForVerification: (options = {}) => {
        console.log('API: Submitting KYC for verification');
        return apiClient.post('/employers/kyc/submit', {
            region: options.region
        });
    },
    forgotPassword: (email) => {
        console.log('API: Sending forgot password request');
        return apiClient.post('/auth/forgot-password', { email });
    },
    resetPassword: (payload) => {
        console.log('API: Sending reset password request');
        return apiClient.post('/auth/reset-password', payload);
    },
    getProfile: () => {
        console.log('API: Fetching user profile');
        return apiClient.get('/auth/profile');
    },
    updateProfile: (profileData) => {
        console.log('API: Updating user profile');
        return apiClient.put('/auth/profile', profileData);
    },
    getKycStatus: () => {
        console.log('API: Fetching KYC status');
        return apiClient.get('/employers/kyc/status');
    },
    getKycRequirements: (region) => {
        console.log(`API: Fetching KYC requirements for region ${region}`);
        return apiClient.get(`/employers/kyc/requirements/${region}`);
    },
    uploadKycDocuments: (files, documentTypeId, options = {}) => {
        console.log('API: Uploading KYC documents');
        const formData = new FormData();
        if (files instanceof FileList || Array.isArray(files)) {
            for (let i = 0; i < files.length; i++) {
                formData.append('documents', files[i]);
            }
        } else if (files instanceof File) {
            formData.append('documents', files);
        } else {
            throw new Error('Invalid file input');
        }
        if (documentTypeId) formData.append('documentTypeId', documentTypeId.toString());
        if (options.documentNumber) formData.append('documentNumber', options.documentNumber);
        if (options.region) formData.append('region', options.region.toUpperCase());
        if (options.subjectType) formData.append('subjectType', options.subjectType);
        if (options.subjectName) formData.append('subjectName', options.subjectName);
        if (options.subjectIndex) formData.append('subjectIndex', String(options.subjectIndex));
        if (options.documentSide) formData.append('documentSide', options.documentSide);
        if (options.instanceKey) formData.append('instanceKey', options.instanceKey);

        return apiClient.post('/employers/kyc/documents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000
        });
    },
    getKycRegions: () => {
        console.log('API: Fetching available KYC regions');
        return apiClient.get('/employers/kyc/regions');
    },
};

// Candidates API
export const candidateAPI = {
    getAll: (params) => {
        console.log('API: Fetching candidates with params:', params);
        return apiClient.get('/candidates', { params });
    },
    getEmployerCandidates: (params = {}) => {
        console.log('API: Fetching employer candidate pipeline with params:', params);
        return apiClient.get('/employers/candidates', { params });
    },
    getEmployerCandidate: (candidateId, params = {}) => {
        console.log(`API: Fetching employer candidate profile ${candidateId}`);
        return apiClient.get(`/employers/candidates/${candidateId}`, { params });
    },
    getById: (id) => {
        console.log(`API: Fetching candidate ${id}`);
        return apiClient.get(`/candidates/${id}`);
    },
    expressInterest: (candidateId, jobId) => {
        console.log(`API: Expressing interest in candidate ${candidateId} for job ${jobId}`);
        return apiClient.post(`/candidates/${candidateId}/express-interest`, { jobId });
    },
    getMatches: (filters) => {
        console.log('API: Fetching candidate matches');
        return apiClient.get('/candidates/matches', { params: filters });
    },
    updateMatchBands: (preferences) => {
        console.log('API: Updating match band preferences');
        return apiClient.put('/candidates/match-bands', preferences);
    }
};

// Interviews API
export const interviewAPI = {
    getAll: (params) => {
        console.log('API: Fetching interviews with params:', params);
        return apiClient.get('/interviews', { params });
    },
    schedule: (interviewData) => {
        console.log('API: Scheduling new interview');
        return apiClient.post('/interviews', interviewData);
    },
    scheduleForMatch: (jobId, matchId, interviewData) => {
        console.log(`API: Scheduling interview for match ${matchId}`);
        return apiClient.post(`/jobs/${jobId}/matches/${matchId}/interviews`, interviewData);
    },
    getForMatch: (jobId, matchId) => {
        console.log(`API: Fetching interviews for match ${matchId}`);
        return apiClient.get(`/jobs/${jobId}/matches/${matchId}/interviews`);
    },
    update: (id, interviewData) => {
        console.log(`API: Updating interview ${id}`);
        return apiClient.put(`/interviews/${id}`, interviewData);
    },
    confirm: (id) => {
        console.log(`API: Confirming interview ${id}`);
        return apiClient.post(`/interviews/${id}/confirm`, { userType: 'employer' });
    },
    cancel: (id, reason) => {
        console.log(`API: Cancelling interview ${id}`);
        return apiClient.post(`/interviews/${id}/cancel`, { reason });
    },
    saveFeedback: (id, feedbackData) => {
        console.log(`API: Saving interview feedback ${id}`);
        return apiClient.put(`/interviews/${id}/feedback`, feedbackData);
    },
    saveMatchFeedback: (jobId, matchId, interviewId, feedbackData) => {
        console.log(`API: Saving match interview feedback ${interviewId}`);
        return apiClient.put(`/jobs/${jobId}/matches/${matchId}/interviews/${interviewId}/employer-feedback`, feedbackData);
    },
    uploadAssessment: (assessmentData) => {
        console.log('API: Uploading assessment');
        const formData = new FormData();
        Object.keys(assessmentData).forEach(key => {
            if (assessmentData[key] instanceof File) {
                formData.append(key, assessmentData[key]);
            } else {
                formData.append(key, JSON.stringify(assessmentData[key]));
            }
        });
        return apiClient.post('/interviews/assessments', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    }
};

export const assessmentAPI = {
    getTypes: () => {
        console.log('API: Fetching assessment types');
        return apiClient.get('/assessments/types');
    },
    getEmployerAssessments: (params = {}) => {
        console.log('API: Fetching employer assessments', params);
        return apiClient.get('/assessments/employer', { params });
    },
    getJobAssessments: (jobId) => {
        console.log(`API: Fetching assessments for job ${jobId}`);
        return apiClient.get(`/jobs/${jobId}/assessments`);
    },
    createForJob: (jobId, assessmentData) => {
        console.log(`API: Creating assessment for job ${jobId}`);
        return apiClient.post(`/jobs/${jobId}/assessments`, assessmentData);
    },
    getForJob: (jobId, assessmentId) => {
        console.log(`API: Fetching assessment ${assessmentId}`);
        return apiClient.get(`/jobs/${jobId}/assessments/${assessmentId}`);
    },
    updateForJob: (jobId, assessmentId, assessmentData) => {
        console.log(`API: Updating assessment ${assessmentId}`);
        return apiClient.put(`/jobs/${jobId}/assessments/${assessmentId}`, assessmentData);
    },
    deactivate: (jobId, assessmentId) => {
        console.log(`API: Deactivating assessment ${assessmentId}`);
        return apiClient.delete(`/jobs/${jobId}/assessments/${assessmentId}`);
    },
    getResults: (jobId, assessmentId, params = {}) => {
        console.log(`API: Fetching assessment results ${assessmentId}`);
        return apiClient.get(`/jobs/${jobId}/assessments/${assessmentId}/results`, { params });
    },
    inviteCandidate: (jobId, matchId, assessmentId, inviteData) => {
        console.log(`API: Inviting match ${matchId} to assessment ${assessmentId}`);
        return apiClient.post(`/jobs/${jobId}/matches/${matchId}/assessments/${assessmentId}/invite`, inviteData);
    },
    inviteCandidates: (jobId, assessmentId, inviteData) => {
        console.log(`API: Bulk inviting candidates to assessment ${assessmentId}`);
        return apiClient.post(`/jobs/${jobId}/assessments/${assessmentId}/invitations`, inviteData);
    },
    reviewAttempt: (jobId, assessmentId, attemptId, reviewData) => {
        console.log(`API: Reviewing assessment attempt ${attemptId}`);
        return apiClient.put(`/jobs/${jobId}/assessments/${assessmentId}/attempts/${attemptId}/review`, reviewData);
    }
};

// Offers API
export const offerAPI = {
    getEmployerOffers: (params = {}) => {
        console.log('API: Fetching employer offers with params:', params);
        return apiClient.get('/offers/employer', { params });
    },
    getAll: (params = {}) => {
        console.log('API: Fetching employer offers with params:', params);
        return apiClient.get('/offers/employer', { params });
    },
    getCandidateOffers: (params = {}) => {
        console.log('API: Fetching candidate offers with params:', params);
        return apiClient.get('/offers/candidate', { params });
    },
    getDetails: (offerId) => {
        console.log(`API: Fetching offer ${offerId}`);
        return apiClient.get(`/offers/${offerId}`);
    },
    create: (offerData) => {
        console.log('API: Creating new offer');
        return apiClient.post('/offers', buildOfferFormData(offerData), {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    createForMatch: (jobId, matchId, offerData) => {
        console.log(`API: Creating offer for match ${matchId}`);
        return apiClient.post(`/jobs/${jobId}/matches/${matchId}/offers`, buildOfferFormData(offerData), {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    withdraw: (id, payload) => {
        console.log(`API: Withdrawing offer ${id}`);
        const body = typeof payload === 'string' ? { reason: payload } : payload;
        return apiClient.post(`/offers/${id}/withdraw`, body);
    },
    respondToNegotiation: (offerId, negotiationId, responseData) => {
        console.log(`API: Responding to negotiation ${negotiationId}`);
        return apiClient.post(`/offers/${offerId}/negotiations/${negotiationId}/respond`, responseData);
    },
    candidateRespond: (offerId, responseData) => {
        console.log(`API: Candidate responding to offer ${offerId}`);
        return apiClient.post(`/offers/${offerId}/respond`, buildOfferFormData(responseData), {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    submitSignedLetter: (offerId, payload) => {
        console.log(`API: Uploading signed letter for offer ${offerId}`);
        return apiClient.post(`/offers/${offerId}/signed-letter`, buildOfferFormData(payload), {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    getOnboardingTasks: (offerId) => {
        console.log(`API: Fetching onboarding tasks for offer ${offerId}`);
        return apiClient.get(`/offers/${offerId}/onboarding`);
    }
};

// Employer/Company Profile API
export const employerAPI = {
    getKycStatus: () => {
        console.log('API: Fetching KYC status');
        return apiClient.get('/employers/kyc/status');
    },
    getKycRequirements: (region) => {
        console.log(`API: Fetching KYC requirements for region ${region}`);
        return apiClient.get(`/employers/kyc/requirements/${region}`);
    },
    uploadKycDocuments: (files, documentTypeId, options = {}) => {
        console.log('API: Uploading KYC documents');
        const formData = new FormData();
        if (files instanceof FileList || Array.isArray(files)) {
            for (let i = 0; i < files.length; i++) {
                formData.append('documents', files[i]);
            }
        } else if (files instanceof File) {
            formData.append('documents', files);
        } else {
            throw new Error('Invalid file input');
        }
        if (documentTypeId) formData.append('documentTypeId', documentTypeId.toString());
        if (options.documentNumber) formData.append('documentNumber', options.documentNumber);
        if (options.region) formData.append('region', options.region.toUpperCase());
        if (options.subjectType) formData.append('subjectType', options.subjectType);
        if (options.subjectName) formData.append('subjectName', options.subjectName);
        if (options.subjectIndex) formData.append('subjectIndex', String(options.subjectIndex));
        if (options.documentSide) formData.append('documentSide', options.documentSide);
        if (options.instanceKey) formData.append('instanceKey', options.instanceKey);

        return apiClient.post('/employers/kyc/documents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000
        });
    },
    getProfile: () => {
        console.log('API: Fetching employer profile');
        return apiClient.get('/employers/profile');
    },
    getProfileSetup: () => {
        console.log('API: Fetching employer profile setup');
        return apiClient.get('/employers/profile/setup');
    },
    getProfileQuestions: () => {
        console.log('API: Fetching employer profile questions');
        return apiClient.get('/employers/profile/questions');
    },
    getProfileAnswers: () => {
        console.log('API: Fetching employer profile answers');
        return apiClient.get('/employers/profile/answers');
    },
    saveProfileAnswers: (answers) => {
        console.log('API: Saving employer profile answers');
        return apiClient.put('/employers/profile/answers', { answers });
    },
    createProfile: (profileData) => {
        console.log('API: Creating employer profile');
        return apiClient.post('/employers/profile', profileData);
    },
    updateProfile: (profileData) => {
        console.log('API: Updating employer profile');
        return apiClient.put('/employers/profile', profileData);
    },
    uploadLogo: (file) => {
        console.log('API: Uploading company logo');
        const formData = new FormData();
        formData.append('logo', file);
        return apiClient.post('/employers/profile/logo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    getProfileSections: () => {
        console.log('API: Fetching employer profile sections');
        return apiClient.get('/employers/profile/sections');
    },

    updateProfileSections: (sections) => {
        console.log('API: Updating employer profile sections');
        return apiClient.put('/employers/profile/sections', { sections });
    },

    deleteProfileSection: (sectionId) => {
        console.log(`API: Deleting employer profile section ${sectionId}`);
        return apiClient.delete(`/employers/profile/sections/${sectionId}`);
    }
};

// Company Profile API (Backwards Compatibility)
export const companyAPI = {
    getProfile: () => employerAPI.getProfile(),
    updateProfile: (profileData) => employerAPI.updateProfile(profileData),
    uploadLogo: (file) => employerAPI.uploadLogo(file)
};

// Dashboard API
export const dashboardAPI = {
    getOverview: (timeframe = '90d') => {
        console.log('API: Fetching employer dashboard overview');
        return apiClient.get('/dashboard/overview', {
            params: { timeframe }
        });
    },

    getStats: () => {
        console.log('API: Fetching dashboard stats');
        return apiClient.get('/dashboard/stats');
    },
    getRecentActivity: () => {
        console.log('API: Fetching recent activity');
        return apiClient.get('/dashboard/activity');
    },
    getAnalytics: (dateRange) => {
        console.log('API: Fetching analytics for date range:', dateRange);
        return apiClient.get('/dashboard/analytics', { params: { dateRange } });
    }
};

// Job API
export const jobAPI = {
    getAll: (params) => {
        console.log('API: Fetching jobs with params:', params);
        return apiClient.get('/jobs', { params });
    },
    getEmployerJobs: (params = {}) => {
        console.log('API: Fetching employer jobs with params:', params);
        return apiClient.get('/jobs/employer', { params });
    },
    getById: (id) => {
        console.log(`API: Fetching job ${id}`);
        return apiClient.get(`/jobs/${id}`);
    },
    create: (jobData) => {
        console.log('API: Creating new job');
        return apiClient.post('/jobs', jobData);
    },
    update: (id, jobData) => {
        console.log(`API: Updating job ${id}`);
        return apiClient.put(`/jobs/${id}`, jobData);
    },
    updateStatus: (id, status, reason) => {
        console.log(`API: Updating job ${id} status`);
        return apiClient.patch(`/jobs/${id}/status`, { status, reason });
    },
    delete: (id) => {
        console.log(`API: Deleting job ${id}`);
        return apiClient.delete(`/jobs/${id}`);
    },
    publish: (id) => {
        console.log(`API: Publishing job ${id}`);
        return apiClient.post(`/jobs/${id}/publish`);
    },
    unpublish: (id) => {
        console.log(`API: Unpublishing job ${id}`);
        return apiClient.post(`/jobs/${id}/unpublish`);
    },
    getMatches: (id, filters) => {
        console.log(`API: Fetching matches for job ${id}`);
        return apiClient.get(`/jobs/${id}/matches`, { params: filters });
    },
    getImmediateMatches: (id, params = {}) => {
        console.log(`API: Fetching immediate matches for job ${id}`);
        return apiClient.get(`/jobs/${id}/immediate-matches`, { params });
    },
    getMatchDetails: (jobId, matchId) => {
        console.log(`API: Fetching match ${matchId} for job ${jobId}`);
        return apiClient.get(`/jobs/${jobId}/matches/${matchId}`);
    },
    takeMatchAction: (jobId, matchId, actionData) => {
        console.log(`API: Taking action on match ${matchId}`);
        return apiClient.post(`/jobs/${jobId}/matches/${matchId}/action`, actionData);
    },
    bulkMatchAction: (jobId, actionData) => {
        console.log(`API: Taking bulk match action for job ${jobId}`);
        return apiClient.post(`/jobs/${jobId}/matches/bulk-action`, actionData);
    },
    triggerMatching: (id, options = {}) => {
        console.log(`API: Triggering matching for job ${id}`);
        return apiClient.post(`/jobs/${id}/trigger-matching`, options);
    },
    getProfileSetup: (id) => {
        console.log(`API: Fetching profile setup for job ${id}`);
        return apiClient.get(`/jobs/${id}/profile/setup`);
    },
    getProfileQuestions: (id) => {
        console.log(`API: Fetching profile questions for job ${id}`);
        return apiClient.get(`/jobs/${id}/profile/questions`);
    },
    getProfileAnswers: (id) => {
        console.log(`API: Fetching profile answers for job ${id}`);
        return apiClient.get(`/jobs/${id}/profile/answers`);
    },
    saveProfileAnswers: (id, answers) => {
        console.log(`API: Saving profile answers for job ${id}`);
        return apiClient.put(`/jobs/${id}/profile/answers`, { answers });
    },
    getAnalytics: (params = {}) => {
        console.log('API: Fetching job analytics');
        return apiClient.get('/jobs/employer/analytics', { params });
    },
    getJobAnalytics: (id, params = {}) => {
        console.log(`API: Fetching analytics for job ${id}`);
        return apiClient.get(`/jobs/${id}/analytics`, { params });
    },
    getMatchingPreferences: () => {
        console.log('API: Fetching matching preferences');
        return apiClient.get('/jobs/matching/preferences');
    },
    updateMatchingPreferences: (preferences) => {
        console.log('API: Updating matching preferences');
        return apiClient.put('/jobs/matching/preferences', preferences);
    },
    getMatchingConfiguration: () => {
        console.log('API: Fetching matching configuration');
        return apiClient.get('/jobs/matching/configuration');
    },
    updateMatchingConfiguration: (configuration) => {
        console.log('API: Updating matching configuration');
        return apiClient.put('/jobs/matching/configuration', configuration);
    },
    getTemplates: () => {
        console.log('API: Fetching job templates');
        return apiClient.get('/jobs/templates');
    },
    parseJobDescription: (file) => {
        console.log('API: Parsing job description file');
        const formData = new FormData();
        formData.append('file', file);
        return apiClient.post('/jobs/parse-jd', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    getAssessments: (jobId) => assessmentAPI.getJobAssessments(jobId),
    createAssessment: (jobId, assessmentData) => assessmentAPI.createForJob(jobId, assessmentData),
    updateAssessment: (jobId, assessmentId, assessmentData) => assessmentAPI.updateForJob(jobId, assessmentId, assessmentData),
    deleteAssessment: (jobId, assessmentId) => assessmentAPI.deactivate(jobId, assessmentId),
    getAssessmentResults: (jobId, assessmentId) => assessmentAPI.getResults(jobId, assessmentId),
    inviteAssessmentCandidate: (jobId, matchId, assessmentId, inviteData) => assessmentAPI.inviteCandidate(jobId, matchId, assessmentId, inviteData)
};

// Export helper functions
export const checkAPIHealth = async () => {
    try {
        console.log('🏥 Checking API health...');
        const response = await apiClient.get('/health');
        console.log('✅ API is healthy');
        return { healthy: true, data: response };
    } catch (error) {
        console.error('❌ API health check failed:', error);
        return { healthy: false, error };
    }
};

export const setAuthToken = (token) => {
    if (token) {
        localStorage.setItem('authToken', token);
        localStorage.setItem('token', token);
        console.log('🔑 Auth token set');
    } else {
        localStorage.removeItem('authToken');
        localStorage.removeItem('token');
        console.log('🔑 Auth token cleared');
    }
};

export default apiClient;
